import * as angular from 'angular';
import * as _ from 'lodash';
import * as moment from 'moment';

import {
    ApplicationUserViewModel,
    ComponentDefinition,
    Dictionary,
    Notification,
    NotificationType,
    OnlineUserDto,
    Pattern,
    Workspace,
} from '../../models';
import {
    IAuthentication,
    INotificationHubService,
    INotificationService,
    IWorkspaceService,
} from '../../services/general/';
import { errorTextColor, warningTextColor, infoTextColor } from '../../common/constants';
import { notificationReceived, tokenize } from '../../common';
import { IExportToCsvService, IStoreService, IUserSettingsService } from '../../services';
import { Store, StoreElement } from '../../stateManagement';
import { INotificationViewModel } from './INotificationViewModel';
import { getNotifificationDataTable } from './getNotificationDataTable';
import { notificationsSearchCriteria } from './notificationSearchCriteria';

class NotificationBadge implements angular.IComponentController {
    auth: Readonly<IAuthentication>;
    dropdownId = 'notification-menu';
    isOpen = false;
    newNotificationCount = 0;
    notifications: Notification[];
    notificationViewModels: INotificationViewModel[];
    onlineUsers: Dictionary<OnlineUserDto>;
    onlineUsersCount: number;
    searchCriteria: Pattern<INotificationViewModel>[];
    searchString: string;
    showAck = false;
    style: any;
    users: Dictionary<ApplicationUserViewModel>;
    toggledDisplayError = false;
    toggledDisplayHistorian = false;
    toggledDisplayInfo = false;
    toggledDisplayLogbook = false;
    toggledDisplayTask = false;
    toggledDisplayWarning = false;

    readonly key = 'notification.searchString';
    readonly tabHeaders: any[];

    private _timer: any;
    private _unsubscribe: (() => void)[] = [];

    static $inject = [
        '$scope',
        '$rootScope',
        '$state',
        '$translate',
        '$timeout',
        'notificationService',
        'storeService',
        'notificationHubService',
        'workspaceService',
        'exportToCsvService',
        'userSettingsService',
    ];

    constructor(
        private _$scope: angular.IScope,
        private _$rootScope: angular.IRootScopeService,
        private _$state: angular.ui.IStateService,
        private _$translate: angular.translate.ITranslateService,
        private _$timeout: angular.ITimeoutService,
        private _notificationService: INotificationService,
        private _storeService: IStoreService,
        private _notificationHubService: INotificationHubService,
        private _workspaceService: IWorkspaceService,
        private _exportToCsvService: IExportToCsvService,
        private _userSettingsService: IUserSettingsService
    ) {
        this.notifications = [];
        this.notificationViewModels = [];
        this.style = {};
        this.tabHeaders = [
            { text: 'Notifications', icon: 'fa-info-circle' },
            { text: 'Online users', icon: 'fa-user-circle-o' },
        ];
        this._notificationService
            .getAll()
            .then((data) => {
                this.notifications = data;
                this.updateNotificationViewModels();
            })
            .catch((reason) => {
                console.error('Unable to retrieve existing notification');
                console.error(reason);
            });
    }

    $onInit() {
        this.searchCriteria = notificationsSearchCriteria;
        this._unsubscribe.push(
            this._storeService.register(
                (store) => this.refreshStore(store),
                [StoreElement.OnlineUsers, StoreElement.Users]
            )
        );

        this._timer = this._$timeout(() => {
            angular.element(`#${this.dropdownId}`).on('show.bs.dropdown', () => this.onOpen());
            angular.element(`#${this.dropdownId}`).on('hidden.bs.dropdown', () => this.onClose());
        });

        this._unsubscribe.push(
            this._$rootScope.$on(notificationReceived, (e, notification: Notification) => {
                if (this.isShown(notification)) {
                    this.notifications = _.uniq([notification].concat(this.notifications));
                    this.updateNotificationViewModels();
                    this._$scope.$apply();
                }
            })
        );

        this._notificationHubService.connect();
    }

    $onDestroy() {
        this._$timeout.cancel(this._timer);
        this._unsubscribe.forEach((u) => u());
    }

    stopPropagation($event: angular.IAngularEvent) {
        const classes = ($event as any).target.className + ($event as any).target.parentElement.className;

        if (classes.indexOf('dropdown-close') <= 0) {
            $event.stopPropagation();
        }
    }

    navigate(vm: INotificationViewModel) {
        const current = this._workspaceService.getWorkspace();
        const authService = this._storeService.dispatchers().authentication;

        if (!this.auth.isConnected || current.application !== vm.workspace.applicationName) {
            authService.disconnect();
            const workspace: Workspace = {
                application: vm.workspace.applicationName,
                environment: vm.workspace.environment,
                model: vm.workspace.modelName,
            };
            authService
                .connect(this.auth.userName, workspace, vm.workspace.applicationGuid, true)
                .then(() => this.goto(vm));
        } else {
            this.goto(vm);
        }
    }

    delete(vm: INotificationViewModel, $event) {
        this._notificationService.delete(vm.guid).then(() => {
            this.notifications = this.notifications.filter((n) => n.guid !== vm.guid);
            this.updateNotificationViewModels();
            this.refreshNotificationViewModels();
        });

        $event.stopPropagation();
    }

    exportCsv() {
        const dataTable = getNotifificationDataTable(this.notificationViewModels);
        this._exportToCsvService.getCsvFromData(
            dataTable,
            `Notifications_${moment().format('YYYYMMDD_HHmm')}.csv`,
            true
        );
    }

    onSearchChange(value: string) {
        this._userSettingsService.setApplicationSetting(this.key, value);
    }

    addFilterToSearch(type: string) {
        const token = tokenize(this.searchString || '');

        if (type === 'info') {
            this.toggledDisplayInfo = !this.toggledDisplayInfo;
        } else if (type === 'warning') {
            this.toggledDisplayWarning = !this.toggledDisplayWarning;
        } else if (type === 'error') {
            this.toggledDisplayError = !this.toggledDisplayError;
        } else if (type === 'task') {
            this.toggledDisplayTask = !this.toggledDisplayTask;
        } else if (type === 'logbook') {
            this.toggledDisplayLogbook = !this.toggledDisplayLogbook;
        } else if (type === 'historian') {
            this.toggledDisplayHistorian = !this.toggledDisplayHistorian;
        }

        const severities: string[] = [];
        if (this.toggledDisplayInfo) {
            severities.push('Ok');
        }
        if (this.toggledDisplayWarning) {
            severities.push('Warn');
        }
        if (this.toggledDisplayError) {
            severities.push('Error');
        }

        const filters: string[] = [];
        if (this.toggledDisplayLogbook) {
            filters.push('Logbook Entry');
        }
        if (this.toggledDisplayTask) {
            filters.push('Task completed');
        }
        if (this.toggledDisplayHistorian) {
            filters.push('Historian Storage');
        }

        const tokens = this.searchString ? tokenize(this.searchString) : {};
        tokens['s'] = severities.join('|');
        tokens['t'] = filters.join('|');
        const stringArray: string[] = [];
        for (const key of Object.keys(tokens)) {
            if (key && tokens[key]) {
                stringArray.push(`${key}:${tokens[key]}`);
            }
        }
        this.searchString = stringArray.join(' ');
    }

    private onOpen() {
        this.searchString = this._userSettingsService.getApplicationSetting<string>(this.key);
        this.isOpen = true;
        this.refreshNotificationViewModels();
        if (!this._$scope.$$phase && !this._$scope.$root.$$phase) {
            this._$scope.$apply();
        }
    }

    private onClose() {
        this.isOpen = false;
        this.acknowledgeAll();
        if (!this._$scope.$$phase && !this._$scope.$root.$$phase) {
            this._$scope.$apply();
        }
    }

    private refreshStore(store: Store) {
        this.auth = store.authentication.value;
        this.onlineUsers = store.onlineUsers.dictionary;
        this.onlineUsersCount = _.keys(this.onlineUsers).length;
        const users = {};
        _.values(store.users.dictionary).forEach((u) => (users[u.name] = u));
        this.users = users;
    }

    private acknowledgeAll() {
        if (_.some(this.notifications, (n) => !n.isAcknowledged)) {
            this._notificationService
            .acknowledge(this.notifications)
            .then(() => {
                this.notifications.forEach((n) => (n.isAcknowledged = true));
                this.updateNotificationViewModels();
            });
        }
    }

    private isNotificationStatus(status: number) {
        return _.some(this.notifications, (n) => !n.isAcknowledged && n.payload && n.payload.status === status);
    }

    private isNotificationError(): boolean {
        return this.isNotificationStatus(2);
    }

    private isNotificationWarning(): boolean {
        return this.isNotificationStatus(1);
    }

    private updateNotificationViewModels() {
        const color = this.isNotificationError()
            ? errorTextColor
            : this.isNotificationWarning()
            ? warningTextColor
            : infoTextColor;

        this.style = { 'background-color': color };
        this.notificationViewModels = this.notifications.map((d) => this.createNotificationViewModel(d));
        this.newNotificationCount = this.notifications.filter((f) => !f.isAcknowledged).length;
    }

    private createNotificationViewModel(d: Notification): INotificationViewModel {
        let prefix: string;
        switch (d.type) {
            case NotificationType.TaskCompleted:
                prefix = 'Task completed';
                break;
            default:
                prefix = '';
        }

        const params = this.getParams(d);
        const messages = d.payload?.message.split('\r\n').map((message) => {
            const m = this._$translate.instant(message, params);
            return prefix === '' ? m : `${this._$translate.instant(prefix)} : ${m}`;
        });

        return {
            notificationDate: d.notificationDate,
            delay: moment(d.notificationDate).fromNow(),
            classed: {
                'acknowledged': d.isAcknowledged,
                'alert-danger': d.payload?.status === 2,
                'alert-warning': d.payload?.status === 1,
                'alert-success': false,
            },
            messages: messages,
            messageParam: {},
            iconClass: this.getIconClass(d.type),
            applicationName: d.workEnvironment.applicationName,
            type: d.type,
            isAcknowledged: d.isAcknowledged,
            workspace: d.workEnvironment,
            guid: d.guid,
            params: params,
            status: d.payload?.status,
        };
    }

    private getIconClass(type: NotificationType) {
        let iconClass: string;
        switch (type) {
            case 0:
                iconClass = 'fa-clock-o';
                break;
            case 1:
                iconClass = 'fa-book';
                break;
            case 2:
                iconClass = 'fa-upload';
                break;
            default:
                iconClass = '';
                break;
        }
        return iconClass;
    }

    private getParams(d: Notification) {
        const params = d.payload?.params ? JSON.parse(d.payload.params) : {};
        if (d.payload?.connectionPointName) {
            params['connectionPointName'] = d.payload.connectionPointName;
        }
        if (d.payload && d.payload.results && params) {
            if (Object.keys(params).indexOf('number') === -1 || params['number'] === 0) {
                params['number'] = d.payload.results['number'];
            }
            if (Object.keys(params).indexOf('number') === -1 || !params['number']) {
                params['number'] = 0;
            }
        }
        return params;
    }

    private refreshNotificationViewModels() {
        this.notificationViewModels.forEach((n) => (n.delay = moment(n.notificationDate).fromNow()));
    }

    private goto(vm: INotificationViewModel) {
        const params = {};
        let page = undefined;

        switch (vm.type) {
            case NotificationType.JobFailed:
                page = 'jobsDefinition';
                break;
            case NotificationType.TaskCompleted:
                page = 'jobsDefinition';
                break;
            case NotificationType.LogbookEntry:
                page = 'logbook';
                params['beginDate'] = vm.params.beginDateTime;
                params['endDate'] = vm.params.endDateTime;
                break;
            default:
        }

        if (page) {
            this._$state.go(page, params, { reload: true });
        }
    }

    private isShown(d: Notification) {
        return !d.isVolatile;
    }
}

export const wtNotificationBadge: ComponentDefinition = {
    componentId: 'wtNotificationBadge',
    controller: NotificationBadge,
    template: require('./notificationBadge.component.html'),
    controllerAs: 'vm',
    transclude: true,
    bindings: {},
};